import { useQueryClient } from "@tanstack/react-query";
import {
    CACHE_INVALIDATION_SUBJECT,
    CacheInvalidationEvent,
} from "@common/types/cache-invalidation-nats";
import { billingQueryKeys } from "@hooks/useBillingStatus";
import { useNatsSubscription } from "@hooks/useNatsSubscription";
import { useScope } from "@state/scopeContext.lib";

/**
 * Global cache-invalidation listener. Mounted once in MainInterface so it is
 * always active for any workspace view, regardless of which feature is open.
 *
 * Each event type is a narrowed, typed object — no loose bags, no dynamic
 * action arrays. Adding a new event = define an interface + union member in
 * `common/types/cache-invalidation-nats.ts`, then add an `if` branch below.
 */
export function useCacheInvalidation() {
    const { workspaceId } = useScope();
    const queryClient = useQueryClient();

    useNatsSubscription<{ data: CacheInvalidationEvent }>(
        workspaceId
            ? `workspaces.${workspaceId}.notifications.${CACHE_INVALIDATION_SUBJECT}`
            : null,
        {
            onMessage: ({ data: event }) => {
                if (event.type === "workspace.status.changed") {
                    // Refetch billing status so useBillingStatus returns an
                    // up-to-date transactionCount after the entity threshold
                    // recalc job marks a new entity at 250 transactions.
                    void queryClient.invalidateQueries({
                        queryKey: billingQueryKeys.all(),
                    });
                }
                // Unknown event types are silently ignored — allows the backend
                // to publish new types before the FE is updated to handle them.
            },
            onError: (error) => {
                // eslint-disable-next-line no-console
                console.error(
                    "useCacheInvalidation: failed to process NATS message",
                    error,
                );
            },
        },
    );
}
